"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.FieldWriters = void 0;
const core_api_1 = require("@ikegps/core-api");
const anchor_field_1 = require("../fields/anchor.field");
const number_field_1 = require("../fields/number.field");
const capture_orientation_read_1 = require("./capture.orientation.read");
function writeNestedList(collectionField) {
    if (collectionField.value == null) {
        return null;
    }
    return [
        collectionField.value
    ];
}
const toUNIXTime = (date) => date.getTime() / 1000;
function getLocations(capture) {
    if (capture.target == null && capture.device == null) {
        return undefined;
    }
    return {
        target: capture.target,
        device: capture.device
    };
}
function getLaser(capture) {
    if (capture.distance == null) {
        return undefined;
    }
    const output = {
        distance: capture.distance
    };
    if (capture.raw && capture.raw.laser) {
        output['raw'] = capture.raw.laser;
    }
    return output;
}
function getCalibration(calibration) {
    if (calibration == null) {
        return undefined;
    }
    return {
        center: calibration.center,
        distortion: calibration.distortion,
        focal: calibration.focal,
        rotation: calibration.rotation,
        translation: calibration.translation
    };
}
function writeCapture(field, capture, type) {
    const imageType = type === 'image' ? 'image' : 'truesize';
    const calibration = field.collection.state.getCalibration(capture.calibrationid);
    const captureRaw = capture.raw || {};
    return {
        capture: {
            objectId: capture.objectId,
            camera: {
                calibration: getCalibration(calibration),
                imageuuid: capture.uuid,
                zoom: capture.zoom
            },
            device: calibration != null ? calibration.deviceid : undefined,
            timestamp: capture.collectedAt && toUNIXTime(new Date(capture.collectedAt.iso)),
            type: imageType,
            uuid: captureRaw.uuid || capture.uuid,
            laser: getLaser(capture),
            locations: getLocations(capture),
            orientation: capture_orientation_read_1.CaptureOrientationSerializer.toLegacyOrientation(capture)
        }
    };
}
function writeImageField(collectionField) {
    return collectionField.getCaptures().map(capture => writeCapture(collectionField, capture, 'image'));
}
function writeTrueSizeField(collectionField) {
    return collectionField.getCaptures().map(capture => writeCapture(collectionField, capture, 'truesize'));
}
function writePMHeight(collectionField) {
    const collection = collectionField.collection;
    const stateValue = collectionField.stateValue;
    const drawable = collection.getDrawableState(stateValue.captureId).getDrawable(stateValue.drawable);
    const capture = collection.state.getCapture(drawable.captureId);
    if (capture == null) {
        throw new Error(`Field ${collectionField.id}: Unable to find capture: ${drawable.captureId} for drawable: ${drawable.id}`);
    }
    return {
        provider: 'photomeasure',
        value: collectionField.value,
        measurement: capture.objectId,
        drawable: stateValue.drawable
    };
}
function writePMManHeight(collectionField) {
    const collection = collectionField.collection;
    const stateValue = collectionField.stateValue;
    const drawable = collection.getDrawableState(stateValue.captureId).getDrawable(stateValue.drawable);
    const capture = collection.state.getCapture(drawable.captureId);
    if (capture == null) {
        throw new Error(`Field ${collectionField.id}: Unable to find capture: ${drawable.captureId} for drawable: ${drawable.id}`);
    }
    return {
        provider: 'photomeasuremanual',
        value: collectionField.value,
        measurement: capture.objectId,
        drawable: stateValue.drawable
    };
}
function writeVerticalSeparation(collectionField) {
    const collection = collectionField.collection;
    const stateValue = collectionField.stateValue;
    if (!(stateValue === null || stateValue === void 0 ? void 0 : stateValue.captureId) || !(stateValue === null || stateValue === void 0 ? void 0 : stateValue.drawableTop)) {
        return {
            value: collectionField.value
        };
    }
    const drawable = collection.getDrawableState(stateValue.captureId).getDrawable(stateValue.drawableTop);
    const capture = collection.state.getCapture(drawable.captureId);
    if (capture == null) {
        throw new Error(`Field ${collectionField.id}: Unable to find capture: ${drawable.captureId} for drawable: ${drawable.id}`);
    }
    return {
        value: collectionField.value,
        measurement: capture.objectId,
        drawableTop: stateValue === null || stateValue === void 0 ? void 0 : stateValue.drawableTop,
        drawableBottom: stateValue === null || stateValue === void 0 ? void 0 : stateValue.drawableBottom,
        drawable: stateValue === null || stateValue === void 0 ? void 0 : stateValue.drawableVerticalSeparation
    };
}
function writeSpanHeight(collectionField) {
    return {
        provider: 'spanheight',
        value: collectionField.value,
        capture: {
            type: 'spanheight',
            points: collectionField.getCaptures().map(capture => writeCapture(collectionField, capture, 'spanheight'))
        }
    };
}
function writeManualHeight(collectionField) {
    return {
        provider: 'manual',
        value: collectionField.value
    };
}
function writeHeight(collectionField) {
    if (collectionField.isManual()) {
        return writeManualHeight(collectionField);
    }
    if (collectionField.isSpanHeight()) {
        return writeSpanHeight(collectionField);
    }
    if (collectionField.isPhotoMeasureManual()) {
        return writePMManHeight(collectionField);
    }
    if (collectionField.isPhotoMeasure()) {
        return writePMHeight(collectionField);
    }
    throw new Error(`Field ${collectionField.id}: Invalid Height provider "${collectionField.stateValue.provider}"`);
}
function writeManualVector(collectionField) {
    return {
        provider: 'manual',
        value: collectionField.value
    };
}
function writeP2PVector(collectionField) {
    return {
        capture: {
            type: 'pointtopoint',
            points: collectionField.getCaptures().map(capture => writeCapture(collectionField, capture, 'pointtopoint'))
        },
        provider: 'pointtopoint',
        value: collectionField.value
    };
}
function writeCollectionVector(collectionField) {
    return {
        provider: 'collection',
        collectionId: collectionField.stateValue.collectionId,
        value: collectionField.value
    };
}
function writeVector(collectionField) {
    if (collectionField.isManual()) {
        return writeManualVector(collectionField);
    }
    if (collectionField.isPointToPoint()) {
        return writeP2PVector(collectionField);
    }
    if (collectionField.isCollectionLink()) {
        return writeCollectionVector(collectionField);
    }
    throw new Error(`Field ${collectionField.id}: Invalid Vector provider "${collectionField.stateValue.provider}"`);
}
function writeManualAnchor(collectionField) {
    return {
        provider: anchor_field_1.CollectionModelFieldAnchor.PROVIDER_MANUAL,
        value: collectionField.value
    };
}
function writeGpsAnchor(collectionField) {
    const stateValue = collectionField.stateValue;
    return {
        provider: anchor_field_1.CollectionModelFieldAnchor.PROVIDER_GPS,
        value: collectionField.value,
        antennaHeight: collectionField.antennaHeight,
        fix: stateValue.fix,
        module: stateValue.module,
        capturedAt: stateValue.capturedAt,
        undulation: stateValue.undulation
    };
}
function writeManualLocationAnchor(collectionField) {
    return {
        provider: anchor_field_1.CollectionModelFieldAnchor.PROVIDER_MANUAL,
        value: collectionField.value
    };
}
function writeP2PAnchor(collectionField) {
    return {
        capture: {
            type: anchor_field_1.CollectionModelFieldAnchor.PROVIDER_P2P,
            points: collectionField.getCaptures().map(capture => writeCapture(collectionField, capture, 'pointtopoint'))
        },
        provider: anchor_field_1.CollectionModelFieldAnchor.PROVIDER_P2P,
        value: collectionField.value
    };
}
function writeAnchor(collectionField) {
    if (!collectionField.hasValue()) {
        return undefined;
    }
    if (collectionField.tool === anchor_field_1.CollectionModelFieldAnchor.COLLLECTION_TOOL_LOCATION) {
        if (collectionField.isGPS()) {
            return writeGpsAnchor(collectionField);
        }
        if (collectionField.isManual()) {
            return writeManualLocationAnchor(collectionField);
        }
    }
    if (collectionField.tool === anchor_field_1.CollectionModelFieldAnchor.COLLLECTION_TOOL_VECTOR) {
        if (collectionField.isPointToPoint()) {
            return writeP2PAnchor(collectionField);
        }
        if (collectionField.isManual()) {
            return writeManualAnchor(collectionField);
        }
    }
    throw new Error(`Field ${collectionField.id}: Invalid Anchor provider "${collectionField.stateValue.provider}"`);
}
function writeNumber(collectionField) {
    if (collectionField.isManual()) {
        return String(collectionField.value);
    }
    if (collectionField.isBearing()) {
        return {
            provider: number_field_1.CollectionModelFieldNumber.PROVIDER_BEARING,
            capture: writeCapture(collectionField, collectionField.getCaptures()[0], 'truesize').capture,
            value: collectionField.value
        };
    }
    if (collectionField.isBackBearing()) {
        return {
            provider: number_field_1.CollectionModelFieldNumber.PROVIDER_BACK_BEARING,
            capture: writeCapture(collectionField, collectionField.getCaptures()[0], 'truesize').capture,
            value: collectionField.value
        };
    }
    if (collectionField.isSag()) {
        const sagCaptures = number_field_1.CollectionModelFieldNumberSagUtil.getSagCaptures(collectionField);
        return {
            provider: number_field_1.CollectionModelFieldNumber.PROVIDER_SAG,
            value: collectionField.value,
            start: writeCapture(collectionField, sagCaptures.start, 'truesize').capture,
            end: writeCapture(collectionField, sagCaptures.end, 'truesize').capture,
            points: sagCaptures.points.map(p => writeCapture(collectionField, p, 'truesize').capture)
        };
    }
    throw new Error(`Field ${collectionField.id}: Invalid Number provider "${collectionField.stateValue.provider}"`);
}
function writeLocation(collectionField) {
    if (!collectionField.hasValue()) {
        return undefined;
    }
    if (collectionField.isEdited()) {
        return {
            provider: 'edited',
            value: collectionField.value
        };
    }
    if (collectionField.isManual()) {
        return {
            provider: 'manual',
            value: collectionField.value
        };
    }
    if (collectionField.isGPS()) {
        const stateValue = collectionField.stateValue;
        return {
            provider: 'gps',
            value: stateValue.value,
            fix: stateValue.fix,
            antennaHeight: stateValue.antennaHeight,
            module: stateValue.module,
            capturedAt: stateValue.capturedAt,
            undulation: stateValue.undulation
        };
    }
    if (collectionField.isTrueSize()) {
        const [capture] = collectionField.getCaptures();
        return {
            provider: 'truesize',
            value: collectionField.value,
            capture: writeCapture(collectionField, capture, 'truesize').capture
        };
    }
    throw new Error(`Field ${collectionField.id}: Invalid Location provider "${collectionField.stateValue.provider}"`);
}
function writeField(collectionField) {
    return collectionField.value;
}
function writeFileToolField(collectionField) {
    return {
        filename: collectionField.stateValue.filename,
        attachmentId: collectionField.stateValue.attachmentId,
        size: collectionField.stateValue.size,
        metadata: collectionField.stateValue.metadata
    };
}
exports.FieldWriters = {
    [core_api_1.IkeFieldType.DATE]: writeField,
    [core_api_1.IkeFieldType.LOCATION]: writeLocation,
    [core_api_1.IkeFieldType.NESTED_LIST]: writeNestedList,
    [core_api_1.IkeFieldType.NUMBER]: writeNumber,
    [core_api_1.IkeFieldType.SELECT_LIST]: writeField,
    [core_api_1.IkeFieldType.TEXT]: writeField,
    [core_api_1.IkeFieldType.HEIGHT]: writeHeight,
    [core_api_1.IkeFieldType.TRUE_SIZE]: writeTrueSizeField,
    [core_api_1.IkeFieldType.YES_NO]: writeField,
    [core_api_1.IkeFieldType.IMAGE]: writeImageField,
    [core_api_1.IkeFieldType.VECTOR]: writeVector,
    [core_api_1.IkeFieldType.ANCHOR]: writeAnchor,
    [core_api_1.IkeFieldType.VERTICAL_SEPARATION]: writeVerticalSeparation,
    [core_api_1.IkeFieldType.FILE_TOOL]: writeFileToolField
};
//# sourceMappingURL=collection.model.write.field.js.map